import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL roles
export const fetchAllRoles = createAsyncThunk(
  "role/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      // FIXED: Clean GET request - NO query params (matching CompanyUser success pattern)
      const response = await axiosInstance.get(API_ENDPOINTS.ROLE.SELECT_ALL);

      return response.data;
    } catch (error) {
      console.error(
        "❌ Roles API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch roles",
      );
    }
  },
);

// Thunk for fetching role by ID
export const fetchRoleById = createAsyncThunk(
  "role/fetchById",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.ROLE.SELECT}/${roleId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Role not found");
    }
  },
);

// Thunk for adding a new role
export const addRole = createAsyncThunk(
  "role/add",
  async (roleData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.ROLE.INSERT,
        roleData,
      );
      dispatch(fetchAllRoles());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add role",
      );
    }
  },
);

// Thunk for updating an existing role
export const updateRole = createAsyncThunk(
  "role/update",
  async (roleData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.ROLE.UPDATE,
        roleData,
      );
      dispatch(fetchAllRoles()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update role",
      );
    }
  },
);

// Thunk for deleting a role
export const deleteRole = createAsyncThunk(
  "role/delete",
  async (roleId, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.ROLE.DELETE}?id=${roleId}`);
      dispatch(fetchAllRoles());
      return roleId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete role",
      );
    }
  },
);

const roleSlice = createSlice({
  name: "role",
  initialState: {
    rolesList: [], // Flat array for table
    pagination: null, // Full pagination wrapper
    selectedRole: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedRole: (state) => {
      state.selectedRole = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Roles - FIXED with pagination support
      .addCase(fetchAllRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.rolesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch roles";
        console.error("❌ Roles API Failed:", action.payload);
      })
      // Fetch By ID
      .addCase(fetchRoleById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedRole = action.payload.data;
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Role
      .addCase(addRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addRole.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Role
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Role
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedRole } = roleSlice.actions;
export default roleSlice.reducer;
